import React from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { getPartnerDetailsSagaAction } from "../../../../../redux/saga/partner-details/saga-actions";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  ContextualMenu,
  Iconography,
  IconographySizes,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import { LoaderSizes } from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import useProductListPageRoute from "../../../../../hooks/useProductListPageRoute";
import {
  contractFindAllSagaAction,
  allOrdersSagaAction,
} from "../../../../../redux/saga/my-account/saga-actions";
import { getExternalContractSagaAction } from "../../../../../redux/saga/external-contract/saga-actions";
import { getUserListSagaAction } from "../../../../../redux/saga/user/saga-actions";
import TogglePortalAccessModal from "./toggle-portal-access-modal";
import LinkPartnerModal from "./link-partner-modal";
import ToggleStatusModal from "./toggle-status-modal";
import DeletePartnerModal from "./delete-partner-modal";
import { getPartnerLogsSagaAction } from "../../../../../redux/saga/partner-logs/saga-actions";
import "./ManagePartner.scss";
import { getPartnerPoints } from "../../../../../redux/saga/partner-program/calculator/saga-action";
import {
  useSessionCanAccessModule,
  useSessionModuleReadOnly,
  useSessionPermissionsLoaded,
  useSessionCanEditModule,
} from "../../../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../../../utils/permissions/partnerManagementPermissionKeys";
import { PartnerTabPermissionProvider } from "../../../../../contexts/PartnerPermissionContext";
import {
  getPermissionKeyForPartnerTab,
  PARTNER_SIDEBAR_MENU_ITEMS,
  PartnerSidebarMenuItem,
} from "./partnerTabPermissions";
import type { SessionPermissionAction } from "../../../../../utils/permissions/flattenRolePermissions";
import { UnsavedChangesProvider, useUnsavedChanges } from "./unsaved-changes/UnsavedChangesContext";

function tabIsAllowed(
  permissionKey: string,
  roleId: number | undefined,
  loaded: boolean,
  sessionPermissionMap: Record<string, SessionPermissionAction>
): boolean {
  if (!roleId) return true;
  if (!loaded) return true;
  const action = sessionPermissionMap[permissionKey];
  return action === "edit" || action === "view";
}

const ManagePartnerContent = () => {
  const [togglePortalAccessModalOpen, setTogglePortalAccessModalOpen] =
    React.useState<boolean>(false);
  const [linkPartnerModalOpen, setLinkPartnerModalOpen] =
    React.useState<boolean>(false);
  const [deletePartnerModalOpen, setDeletePartnerModalOpen] =
    React.useState<boolean>(false);
  const [toggleStatusModalOpen, setToggleStatusModalOpen] =
    React.useState<boolean>(false);
  const { partnerDetails, status } = useAppSelector(
    (state) => state.partner_details
  );
  const { loggedUserDetail, sessionPermissionMap } = useAppSelector(
    (state) => state.auth
  );
  const { collection } = useAppSelector((state) => state.plp);
  const sessionLoaded = useSessionPermissionsLoaded();
  const roleId = loggedUserDetail?.role_id;
  const canAccessPartnerPortal = useSessionCanAccessModule(
    PM_KEYS.ACCESS_PARTNER_VIEW
  );
  const canAccessPartnerConfig = useSessionCanAccessModule(
    PM_KEYS.PARTNER_CONFIGURATIONS
  );
  const canEditPartnerConfig = useSessionCanEditModule(
    PM_KEYS.PARTNER_CONFIGURATIONS
  );
  /** Link / portal / status / delete — partner_configurations edit only */
  const canUsePartnerConfigActions = canEditPartnerConfig;
  const { requestNavigation } = useUnsavedChanges();

  const partnerSinceDate = partnerDetails?.registration_date
    ? moment(partnerDetails.registration_date).format("D MMM, YYYY")
    : "—";

  const isActivePartner = partnerDetails?.status?.toLowerCase() === "active";

  const { ID } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const pathNameArray = pathName.split("/");
  const currentMenu = pathNameArray[pathNameArray.length - 1];
  const { getRouteIndex, dispatchIt } = useProductListPageRoute();
  const shopifyNavigationHandle = "service-catalog";

  const visibleSidebarMenus = React.useMemo(() => {
    // Partner program tab visibility is permission-driven via `tabIsAllowed`.
    const base: PartnerSidebarMenuItem[] = PARTNER_SIDEBAR_MENU_ITEMS;

    return base.filter(
      (menu) =>
        menu.skipPermissionCheck ||
        tabIsAllowed(
          menu.permissionKey,
          roleId,
          sessionLoaded,
          sessionPermissionMap
        )
    );
  }, [
    roleId,
    sessionLoaded,
    sessionPermissionMap,
  ]);

  const currentMenuItem = PARTNER_SIDEBAR_MENU_ITEMS.find(
    (m) => m.key === currentMenu
  );
  const activePermissionKey = getPermissionKeyForPartnerTab(currentMenu);
  const permissionReadOnly = useSessionModuleReadOnly(activePermissionKey);
  const tabReadOnly = currentMenuItem?.skipPermissionCheck
    ? false
    : permissionReadOnly;

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
    const numberID = Number(ID);
    dispatch(getPartnerDetailsSagaAction(numberID));

    if (collection[getRouteIndex(shopifyNavigationHandle)]?.length === 0) {
      dispatchIt(shopifyNavigationHandle);
    }
  }, []);

  React.useEffect(() => {
    const pathSegments = pathName.split("/");
    const isPartnerRoot =
      pathSegments[pathSegments.length - 2] === "partner-organizations" ||
      pathSegments[pathSegments.length - 1] === "";

    if (roleId && !sessionLoaded) return;
    if (visibleSidebarMenus.length === 0) return;

    if (isPartnerRoot) {
      const defaultTab = visibleSidebarMenus.some((m) => m.key === "dashboard")
        ? "dashboard"
        : visibleSidebarMenus[0].key;
      navigate(defaultTab, { replace: true });
      return;
    }

    if (roleId && sessionLoaded) {
      const allowed = visibleSidebarMenus.some((m) => m.key === currentMenu);
      if (!allowed) {
        navigate(visibleSidebarMenus[0].key, { replace: true });
      }
    }
  }, [
    currentMenu,
    visibleSidebarMenus,
    sessionLoaded,
    roleId,
    navigate,
    pathName,
  ]);

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
    if (partnerDetails) {
      dispatch(getUserListSagaAction(partnerDetails.ID));
      dispatch(contractFindAllSagaAction(partnerDetails.ID));
      dispatch(getExternalContractSagaAction(partnerDetails.ID));
      dispatch(allOrdersSagaAction(partnerDetails.ID));
      dispatch(getPartnerLogsSagaAction(partnerDetails.ID));
      dispatch(getPartnerPoints(partnerDetails.ID));
    }
  }, [partnerDetails]);

  const onMenuItemClick = (menu: string) => {
    if (currentMenu === menu) return;
    requestNavigation(() => navigate(menu));
  };

  const contextualMenuData = [
    {
      data: [
        {
          key: "link-partner",
          value: partnerDetails?.parent_partner
            ? "Unlink from Parent"
            : "Link to Parent",
        },
        {
          key: "toggle-portal-access",
          value:
            partnerDetails?.enable === 0
              ? "Enable Portal Access"
              : "Disable Portal Access",
        },
        {
          key: "toggle-status",
          value:
            partnerDetails?.status === "Inactive"
              ? "Enable Partner"
              : "Disable Partner",
        },
        {
          key: "delete-partner",
          value: "Delete Partner",
        },
      ],
    },
  ];

  const handleMenuItem = (event: any, item: any) => {
    if (!canUsePartnerConfigActions) return;
    if (item.key === "toggle-portal-access") {
      setTogglePortalAccessModalOpen(true);
      setLinkPartnerModalOpen(false);
      setDeletePartnerModalOpen(false);
      setToggleStatusModalOpen(false);
    } else if (item.key === "link-partner") {
      setTogglePortalAccessModalOpen(false);
      setLinkPartnerModalOpen(true);
      setDeletePartnerModalOpen(false);
      setToggleStatusModalOpen(false);
    } else if (item.key === "delete-partner") {
      setTogglePortalAccessModalOpen(false);
      setLinkPartnerModalOpen(false);
      setDeletePartnerModalOpen(true);
      setToggleStatusModalOpen(false);
    } else if (item.key === "toggle-status") {
      setTogglePortalAccessModalOpen(false);
      setLinkPartnerModalOpen(false);
      setDeletePartnerModalOpen(false);
      setToggleStatusModalOpen(true);
    }
  };

  return (
    <div className="manage-partner-container">
      {status.partnerDetailsLoading ? (
        <div className="partner-details-loading">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : partnerDetails ? (
        <>
          <div className="manage-partner-content">
            <Breadcrumbs noTrailingSlash={true}>
              <BreadcrumbItem
                name="Manage HID Partners"
                onClick={() =>
                  requestNavigation(() =>
                    navigate("/hid-admin/dashboard/partner-organizations")
                  )
                }
                breadcrumbSeparator={<Iconography icon="chevronRight" />}
                isCurrentPage={false}
              ></BreadcrumbItem>
              <BreadcrumbItem
                name={partnerDetails.partner_name}
                onClick={() => {}}
                breadcrumbSeparator={<Iconography icon="chevronRight" />}
                isCurrentPage={true}
              ></BreadcrumbItem>
            </Breadcrumbs>

            <div className="all-partner-information">
            <div className="partner-short-details">
              <div className="partner-short-details__identity">
                <div className="partner-logo">
                  <Iconography
                    icon="circleUserFilled"
                    size={IconographySizes.Medium}
                  />
                </div>
                <div className="name-and-details">
                  <div className="name">{partnerDetails.partner_name}</div>
                  <div className="partner-meta-row">
                    <div className="partner-id">
                      Partner ID: {partnerDetails.partner_id}
                    </div>
                    <span className="badge badge--partner-since">
                      Partner Since: <strong>&nbsp;{partnerSinceDate}</strong>
                    </span>
                    <span
                      className={`badge badge--status ${
                        isActivePartner
                          ? "badge--status-active"
                          : "badge--status-inactive"
                      }`}
                    >
                      Status: {partnerDetails.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="partner-short-details__actions">
                {canAccessPartnerPortal && (
                  <Button
                    className="partner-portal-btn"
                    label={
                      <span className="btn-with-icon">
                        Partner Portal
                        <Iconography
                          icon="arrowUpRightFromSquare"
                          size={IconographySizes.Small}
                        />
                      </span>
                    }
                    onClick={() =>
                      requestNavigation(() =>
                        navigate(`/partner/${partnerDetails.ID}/dashboard/home`)
                      )
                    }
                  />
                )}
                {canUsePartnerConfigActions && (
                  <ContextualMenu
                    contextualMenuData={contextualMenuData}
                    onClick={handleMenuItem}
                    isToogle={true}
                    placement={"bottom-end"}
                  >
                    <Button
                      className="partner-actions-menu"
                      variant="secondary"
                      icon={<Iconography icon="ellipsisVertical" />}
                    />
                  </ContextualMenu>
                )}
              </div>
            </div>

            <div className="sidebar-outlet">
              <div className="sidebar">
                {visibleSidebarMenus.map((menu) => (
                  <div
                    className={`menu-item ${
                      currentMenu === menu.key ? "active-menu-item" : ""
                    }`}
                    onClick={() => onMenuItemClick(menu.key)}
                    key={menu.key}
                  >
                    <Iconography
                      icon={menu.icon}
                      size={IconographySizes.Medium}
                    />
                    <p>{menu.label}</p>
                  </div>
                ))}
              </div>
              <div className="outlet">
                <PartnerTabPermissionProvider
                  value={{
                    readOnly: tabReadOnly,
                    permissionKey: activePermissionKey,
                  }}
                >
                  <Outlet />
                </PartnerTabPermissionProvider>
              </div>
            </div>
          </div>
          </div>
        </>
      ) : (
        <div className="partner-title">Partner Not found</div>
      )}
      {canUsePartnerConfigActions && togglePortalAccessModalOpen && (
        <TogglePortalAccessModal
          partnerDetails={partnerDetails}
          setModalOpen={setTogglePortalAccessModalOpen}
        />
      )}
      {canUsePartnerConfigActions && linkPartnerModalOpen && (
        <LinkPartnerModal
          partnerDetails={partnerDetails}
          setModalOpen={setLinkPartnerModalOpen}
        />
      )}
      {canUsePartnerConfigActions && toggleStatusModalOpen && (
        <ToggleStatusModal
          partnerDetails={partnerDetails}
          setModalOpen={setToggleStatusModalOpen}
        />
      )}
      {canUsePartnerConfigActions && deletePartnerModalOpen && (
        <DeletePartnerModal
          partnerDetails={partnerDetails}
          setModalOpen={setDeletePartnerModalOpen}
        />
      )}
    </div>
  );
};

const ManagePartner = () => (
  <UnsavedChangesProvider>
    <ManagePartnerContent />
  </UnsavedChangesProvider>
);

export default ManagePartner;
